import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { notifyError } from "../../utils/notifications";
import { ActivityNcsChart } from "./ActivityNcsChart";
import { AverageNcDaysIndustryCard } from "./AverageNcDaysIndustryCard";
import { AverageNcRatio } from "./AverageNcRatio";
import { BreachTypeNcChart } from "./BreachTypeNcChart";
import { FleetSummary } from "./FleetSummary";
import { HoursPerHeadScatter } from "./HoursPerHeadScatter";
import { NcCrewHotspotTable } from "./NcCrewHotspotTable";
import { NcDaysComparison } from "./NcDaysComparison";
import { NcDaysPerCrewMemberBoxplot } from "./NcDaysPerCrewMemberBoxplot";
import { NcLevelsChart } from "./NcLevelsChart";
import { NcRankHotspotTable } from "./NcRankHotspotTable";
import { PortLocationNcsTable } from "./PortLocationNcsTable";
import { QuarterlyInsightsFilters } from "./QuarterlyInsightsFilters";
import { UnderAverageCrewComplementTable } from "./UnderAverageCrewComplementTable";
import { VesselsAttentionTable } from "./VesselsAttentionTable";
import { VesselsStaleSync } from "./VesselsStaleSync";
import { WorkloadOutliersTable } from "./WorkloadOutliersTable";
const buildQuarterOptions = (today = new Date(), count = 5) => {
    let currentYear = today.getFullYear();
    let currentQuarter = Math.ceil((today.getMonth() + 1) / 3) - 1;
    if (currentQuarter === 0) {
        currentQuarter = 4;
        currentYear -= 1;
    }
    const quarters = [];
    for (let index = 0; index < count; index += 1) {
        quarters.push({
            year: currentYear,
            quarter: currentQuarter,
            label: `Q${currentQuarter} ${currentYear}`,
        });
        currentQuarter -= 1;
        if (currentQuarter === 0) {
            currentQuarter = 4;
            currentYear -= 1;
        }
    }
    return quarters;
};
const getPreviousQuarterLabel = (selectedQuarter) => {
    if (selectedQuarter == null) {
        return "";
    }
    let previousQuarter = selectedQuarter.quarter - 1;
    let previousYear = selectedQuarter.year;
    if (previousQuarter === 0) {
        previousQuarter = 4;
        previousYear -= 1;
    }
    return `Q${previousQuarter} ${previousYear}`;
};
export const QuarterlyInsightsPage = () => {
    return _jsx(QuarterlyInsightsPageContent, { today: new Date() });
};
export const QuarterlyInsightsPageContent = ({ today }) => {
    const quarterOptions = useMemo(() => buildQuarterOptions(today), [today]);
    const [selectedQuarter, setSelectedQuarter] = useState(quarterOptions[0] ?? null);
    const [fleets, setFleets] = useState([]);
    const [selectedFleetIds, setSelectedFleetIds] = useState([]);
    const [isLoadingFleets, setIsLoadingFleets] = useState(false);
    const [fleetLoadError, setFleetLoadError] = useState("");
    useEffect(() => {
        let isDisposed = false;
        setIsLoadingFleets(true);
        setFleetLoadError("");
        fetch("/api/lists/fleets")
            .then((response) => {
            if (!response.ok) {
                throw new Error(response.statusText || "Failed to load fleets");
            }
            return response.json();
        })
            .then((data) => {
            if (isDisposed) {
                return;
            }
            setFleets(data);
            setSelectedFleetIds(data.map((fleet) => fleet.ID));
        })
            .catch((error) => {
            console.error(error);
            if (isDisposed) {
                return;
            }
            setFleetLoadError("Error loading fleet filters");
        })
            .finally(() => {
            if (!isDisposed) {
                setIsLoadingFleets(false);
            }
        });
        return () => {
            isDisposed = true;
        };
    }, []);
    const fleetOptions = useMemo(() => fleets.map((fleet) => ({ value: fleet.ID, label: fleet.Name })), [fleets]);
    const selectedFleetOptions = useMemo(() => fleetOptions.filter((option) => selectedFleetIds.includes(option.value)), [fleetOptions, selectedFleetIds]);
    const dataOptions = useMemo(() => {
        let dataOptions = {
            isReady: false,
            year: 0,
            quarter: 0,
            fleetIds: [],
        };
        if (fleetOptions != null && fleetOptions.length > 0) {
            dataOptions.isReady = true;
        }
        if (selectedQuarter == null) {
            dataOptions.year = 0;
            dataOptions.quarter = 0;
        }
        else {
            dataOptions.year = selectedQuarter.year;
            dataOptions.quarter = selectedQuarter.quarter;
        }
        if (selectedFleetIds.length === 0) {
            dataOptions.fleetIds = [];
        }
        else {
            dataOptions.fleetIds = selectedFleetIds;
        }
        return dataOptions;
    }, [fleetOptions, selectedFleetIds, selectedQuarter]);
    const previousQuarterLabel = useMemo(() => getPreviousQuarterLabel(selectedQuarter), [selectedQuarter]);
    const handleFleetChange = (selected) => {
        if (selected.length === 0) {
            notifyError("One fleet must be selected as a minimum.");
            setSelectedFleetIds((current) => current.slice(0, 1));
            return;
        }
        setSelectedFleetIds(selected.map((option) => option.value));
    };
    return (_jsxs("div", { className: "container", children: [_jsx("div", { className: "row mb-3", children: _jsx("div", { className: "col-12", children: _jsx("div", { className: "card shadow", children: _jsxs("div", { className: "card-body my-2 py-1 d-flex align-items-center gap-3 flex-wrap", children: [_jsx("h1", { className: "mb-0 flex-shrink-0", children: "Quarterly Insights" }), _jsx(QuarterlyInsightsFilters, { quarterOptions: quarterOptions, selectedQuarter: selectedQuarter, onQuarterChange: setSelectedQuarter, fleetOptions: fleetOptions, selectedFleetOptions: selectedFleetOptions, onFleetChange: handleFleetChange, isLoadingFleets: isLoadingFleets })] }) }) }) }), fleetLoadError ? (_jsx("div", { className: "row mb-3", children: _jsx("div", { className: "col-12", children: _jsx("div", { className: "alert alert-danger mb-0", role: "alert", children: fleetLoadError }) }) })) : null, _jsx("div", { className: "row mb-4", children: _jsx("div", { className: "col-12", children: _jsx(FleetSummary, { options: dataOptions }) }) }), _jsxs("div", { children: [_jsx("h2", { className: "mb-3", children: "Fleet Health Snapshot" }), _jsxs("div", { className: "row g-3", children: [_jsx("div", { className: "col-12 col-md-6 col-lg-3", children: _jsx(NcDaysComparison, { options: dataOptions, previousQuarterLabel: () => previousQuarterLabel }) }), _jsx("div", { className: "col-12 col-md-6 col-lg-3", children: _jsx(AverageNcRatio, { options: dataOptions }) }), _jsx("div", { className: "col-12 col-md-6 col-lg-3", children: _jsx(AverageNcDaysIndustryCard, { options: dataOptions }) }), _jsx("div", { className: "col-12 col-md-6 col-lg-3", children: _jsx(NcLevelsChart, { options: dataOptions }) })] })] }), _jsx("div", { className: "row mt-4", children: _jsx("div", { className: "col-12", children: _jsxs("div", { children: [_jsx("h2", { className: "mb-3", children: "Vessels Requiring Attention" }), _jsxs("div", { className: "row g-3", children: [_jsx("div", { className: "col-12 col-md-8", children: _jsx(VesselsAttentionTable, { options: dataOptions }) }), _jsx("div", { className: "col-12 col-md-4", children: _jsx(VesselsStaleSync, { options: dataOptions }) })] })] }) }) }), _jsx("div", { className: "row mt-4", children: _jsx("div", { className: "col-12", children: _jsxs("div", { children: [_jsx("h2", { className: "mb-3", children: "Crew and Ranks Hotspots" }), _jsxs("div", { className: "row g-3", children: [_jsx("div", { className: "col-12 col-md-5", children: _jsx(NcRankHotspotTable, { options: dataOptions }) }), _jsx("div", { className: "col-12 col-md-7", children: _jsx(NcCrewHotspotTable, { options: dataOptions }) })] })] }) }) }), _jsx("div", { className: "row mt-4", children: _jsx("div", { className: "col-12", children: _jsxs("div", { children: [_jsx("h2", { className: "mb-3", children: "Crewing & Workload" }), _jsxs("div", { className: "row g-3", children: [_jsx("div", { className: "col-12 col-md-6", children: _jsx(NcDaysPerCrewMemberBoxplot, { options: dataOptions }) }), _jsx("div", { className: "col-12 col-md-6", children: _jsx(HoursPerHeadScatter, { options: dataOptions }) })] }), _jsxs("div", { className: "row g-3 mt-1", children: [_jsx("div", { className: "col-12 col-md-6", children: _jsx(WorkloadOutliersTable, { options: dataOptions }) }), _jsx("div", { className: "col-12 col-md-6", children: _jsx(UnderAverageCrewComplementTable, { options: dataOptions }) })] })] }) }) }), _jsx("div", { className: "row mt-4", children: _jsx("div", { className: "col-12", children: _jsxs("div", { children: [_jsx("h2", { className: "mb-3", children: "Operational Patterns" }), _jsxs("div", { className: "row g-3", children: [_jsx("div", { className: "col-12 col-md-6", children: _jsxs("div", { className: "row g-3", children: [_jsx("div", { className: "col-12", children: _jsx(PortLocationNcsTable, { options: dataOptions }) }), _jsx("div", { className: "col-12", children: _jsx(ActivityNcsChart, { options: dataOptions }) })] }) }), _jsx("div", { className: "col-12 col-md-6", children: _jsx(BreachTypeNcChart, { options: dataOptions }) })] })] }) }) })] }));
};
